import React, { useContext, useEffect, useState } from 'react';
import { Modal, Button, message } from 'antd';
import PropTypes from 'prop-types';
import './SubscriptionModal.css';
import { GENERIC_ERROR_MESSAGE } from 'utils/constants';
import SubscriptionContext from 'context/SubscriptionContext';

/**
 * Confirmation modal for upgrading to a higher plan.
 *
 * The figures shown come from the upgrade preview endpoint rather than being
 * computed here, so what the store is told matches exactly what the backend will
 * do — including how many unused meetings carry across.
 *
 * @param {Boolean} open whether the modal is visible
 * @param {object} plan the plan being upgraded to
 * @param {object} activePlan the store's current subscription record
 * @param {Number} storeId the store identifier
 * @param {Function} handleClose closes the modal
 * @param {Function} onUpgraded called after a successful upgrade
 * @return {ReactNode} the upgrade confirmation modal
 */

const UpgradeSubscriptionModal = ({
  open,
  plan,
  activePlan,
  storeId,
  handleClose,
  onUpgraded,
}) => {
  const { upgradeSubscriptionForStore, previewUpgradeForStore } =
    useContext(SubscriptionContext);
  const [messageApi, contextHolder] = message.useMessage();

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  /**
   * Fetch the preview whenever the modal opens for a plan.
   *
   * @returns {void}
   */
  useEffect(() => {
    const loadPreview = async () => {
      if (!open || !plan?.id) {
        return;
      }

      setPreviewLoading(true);
      try {
        const res = await previewUpgradeForStore(plan.id);
        setPreview(res?.data || null);
      } catch (e) {
        console.log(e);
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreview();
  }, [open, plan?.id]);

  /**
   * Perform the upgrade and hand control back to the page to refresh.
   *
   * @returns {void}
   */

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const res = await upgradeSubscriptionForStore({
        store_identifier: storeId,
        subscription_type: plan?.id,
      });

      messageApi.open({
        type: 'success',
        content: res?.data?.message || 'Subscription upgraded successfully',
        duration: 5,
      });

      handleClose();

      if (onUpgraded) {
        await onUpgraded();
      }
    } catch (e) {
      console.log(e);
      messageApi.open({
        type: 'error',
        content:
          e?.response?.data?.message || e?.message || GENERIC_ERROR_MESSAGE,
        duration: 5,
      });
    } finally {
      setUpgradeLoading(false);
    }
  };

  const carriedForward = preview?.carried_forward_calls || 0;
  const canUpgrade = preview ? preview.can_upgrade : true;

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        centered
        className="subscription-modal"
        onCancel={handleClose}
        footer={null}
        destroyOnClose
      >
        <div className="subscription-modal-body">
          Upgrade to {plan?.name}?
          {previewLoading ? (
            <div className="pause-sub-body">Loading details…</div>
          ) : (
            <>
              {!canUpgrade && (
                <div className="pause-sub-body">
                  {preview?.reason ||
                    'This plan is not available to upgrade to.'}
                </div>
              )}

              {canUpgrade && (
                <div className="pause-sub-body">
                  Your {activePlan?.subscription_type?.name} subscription ends
                  now and {plan?.name} starts immediately.
                  <ul className="upgrade-summary-list">
                    <li>
                      You will be charged{' '}
                      <strong>${Number(plan?.monthly_fees || 0)}</strong> for
                      the first month of {plan?.name}.
                    </li>
                    <li>
                      Partnership fees for your current cycle will be charged up
                      to today.
                    </li>
                    <li>
                      {carriedForward > 0 ? (
                        <>
                          Your <strong>{carriedForward}</strong> unused meeting
                          {carriedForward === 1 ? '' : 's'} will carry over to
                          the new plan.
                        </>
                      ) : (
                        'You have no unused meetings to carry over.'
                      )}
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
        <div className="subscription-modal-buttons">
          <Button className="cancel-sub-modal-button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="sub-selection-button"
            loading={upgradeLoading}
            disabled={previewLoading || !canUpgrade}
            onClick={handleUpgrade}
          >
            Confirm Upgrade
          </Button>
        </div>
      </Modal>
    </>
  );
};

UpgradeSubscriptionModal.propTypes = {
  open: PropTypes.bool,
  plan: PropTypes.object,
  activePlan: PropTypes.object,
  storeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleClose: PropTypes.func,
  onUpgraded: PropTypes.func,
};

export default UpgradeSubscriptionModal;
